import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Settings as SettingsIcon, Save, Languages,
} from "lucide-react";
import { PILL, Field, Input } from "./shared";
import TypographyControls from "./TypographyControls";
import MediaManager from "./MediaManager";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish · Español" },
  { code: "fr", label: "French · Français" },
  { code: "de", label: "German · Deutsch" },
  { code: "pt", label: "Portuguese · Português" },
  { code: "it", label: "Italian · Italiano" },
  { code: "ja", label: "Japanese · 日本語" },
  { code: "hi", label: "Hindi · हिन्दी" },
];

export default function SettingsTab({ biz, reload }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ notification_email: "", business_name: "", business_category: "", language: "en" });
  const [typography, setTypography] = useState({});
  const [pageBgColor, setPageBgColor] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/business/settings");
        setSettings(data);
        setForm({
          notification_email: data.notification_email || "",
          business_name: data.business_name || "",
          business_category: data.business_category || "",
          language: data.language || "en",
        });
        setTypography(data.typography || {});
        setPageBgColor(data.page_bg_color || "");
      } catch { toast.error("Couldn't load settings"); }
      setLoading(false);
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.put("/business/settings", { ...form, typography, page_bg_color: pageBgColor });
      setSettings(data);
      setTypography(data.typography || {});
      setPageBgColor(data.page_bg_color || "");
      toast.success("Settings saved");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    setSaving(false);
  };

  if (loading) return <div className="nb-card-flat p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div>
      <div className="nb-card p-5 mb-6">
        <div className="font-display text-2xl font-extrabold flex items-center gap-2"><SettingsIcon className="w-6 h-6" /> Website Settings</div>
        <div className="text-sm text-gray-600 mt-1">Settings that apply to this specific website.</div>
      </div>
      <form onSubmit={save} className="nb-card p-5 space-y-4 max-w-2xl" data-testid="settings-form">
        <Field label="Business name">
          <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} testId="setting-business-name" />
        </Field>
        <Field label="Business category">
          <Input value={form.business_category} onChange={(e) => setForm({ ...form, business_category: e.target.value })} testId="setting-business-category" placeholder="e.g. Coaching, Bakery, Web design" />
        </Field>
        <Field label="Notification email">
          <Input type="email" value={form.notification_email} onChange={(e) => setForm({ ...form, notification_email: e.target.value })} testId="setting-notification-email" placeholder="orders@your-domain.com" />
          <div className="text-xs text-gray-500 mt-1">All contact form messages, order confirmations, and new bookings for this website will go to this email.</div>
        </Field>

        <div className="border-t-2 border-black/10 pt-4">
          <div className="text-xs uppercase font-bold text-gray-600 mb-2 flex items-center gap-2"><Languages className="w-4 h-4" /> Website language</div>
          <p className="text-xs text-gray-500 mb-3">
            Your language preference is saved with your other settings. Site-wide retro-translation is
            being rebuilt for the new content architecture and will return in a future update.
          </p>
          <select
            value={form.language || "en"}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
            data-testid="setting-language"
            className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm bg-white"
          >
            {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>

        <div className="border-t-2 border-black/10 pt-4">
          <div className="text-xs uppercase font-bold text-gray-600 mb-2">Site-wide typography</div>
          <p className="text-xs text-gray-500 mb-3">
            These defaults apply to every section on every page. Individual sections can still override
            them in the page editor.
          </p>
          <TypographyControls
            value={typography}
            onChange={setTypography}
            scopeLabel="Site-wide"
            testIdPrefix="global-typo"
          />
        </div>

        <div className="border-t-2 border-black/10 pt-4">
          <div className="text-xs uppercase font-bold text-gray-600 mb-2">Page background color</div>
          <p className="text-xs text-gray-500 mb-2">
            Applied to the entire website (every page) as the base background. Individual sections can still
            override their own background color.
          </p>
          <div className="flex items-center gap-2">
            <input type="color" value={pageBgColor || "#FFFFFF"} onChange={(e) => setPageBgColor(e.target.value)}
              className="w-10 h-10 border-2 border-black rounded cursor-pointer" data-testid="page-bg-color" />
            <input type="text" value={pageBgColor || ""} placeholder="Default (theme)"
              onChange={(e) => setPageBgColor(e.target.value)}
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg text-sm" />
            {pageBgColor && <button type="button" onClick={() => setPageBgColor("")} className="text-xs font-bold underline text-gray-600">clear</button>}
          </div>
        </div>

        <div className="border-t-2 border-black/10 pt-4 text-sm space-y-1 text-gray-700">
          <div><b>Website subdomain:</b> /{settings?.slug}</div>
          {settings?.custom_domain && <div><b>Custom domain:</b> {settings.custom_domain}</div>}
          {settings?.style && <div><b>Style:</b> {settings.style}</div>}
        </div>
        <button disabled={saving} className={`${PILL} bg-[#8B5CF6] text-white`} data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save settings</>}
        </button>
      </form>

      {/* Universal media manager — hero slot on this site */}
      {biz?.industry_template !== "restaurant" && (
        <div className="nb-card p-5 mt-6" data-testid="settings-media-panel">
          <MediaManager biz={biz} />
        </div>
      )}
    </div>
  );
}
